// hooks/useNotifications.ts
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  AppNotification,
  getNotifications,
  getUnreadCount,
  markAllNotificationsRead,
  markNotificationRead,
  deleteNotification,
  NotificationType,
} from '../../services/notification.service';

const POLL_INTERVAL = 30_000; 

export function useNotifications() {
  const [items, setItems] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [typeFilter, setTypeFilter] = useState<NotificationType | 'all'>('all');

  const intervalRef = useRef<number | null>(null);

  const load = useCallback(
    async (opts: { reset?: boolean } = {}) => {
      try {
        setError(null);
        if (opts.reset) setLoading(true);
        const targetPage = opts.reset ? 1 : page;
        const data = await getNotifications({
          page: targetPage,
          limit: 15,
          filter,
          type: typeFilter,
        });
        setItems((prev) => (opts.reset ? data.items : [...prev, ...data.items]));
        setUnreadCount(data.unreadCount);
        setHasMore(data.pagination.page < data.pagination.pages);
        if (opts.reset) setPage(1);
      } catch (err: any) {
        setError(err?.message || 'Failed to load notifications');
      } finally {
        setLoading(false);
      }
    },
    [page, filter, typeFilter]
  );

  // initial + when filter changes
  useEffect(() => {
    load({ reset: true });
  }, [filter, typeFilter]); 

  // polling for badge count
 useEffect(() => {
  const tick = async () => {
      try {
        const count = await getUnreadCount();
        setUnreadCount(count);
      } catch {
        
      }
    };
    tick();
    intervalRef.current = window.setInterval(tick, POLL_INTERVAL);
    return () => {
      if (intervalRef.current !== null) {
        window.clearInterval(intervalRef.current);
      }
    };
  }, []);

  const markRead = useCallback(async (id: string) => {
    const notification = items.find((n) => n.id === id);
    if (!notification || notification.isRead) return;
    setItems((prev) =>
      prev.map((n) =>
        n.id === id ? { ...n, isRead: true } : n
      )
    );
    setUnreadCount((c) => Math.max(0, c - 1));
    try {
      await markNotificationRead(id);
    } catch {
      setItems((prev) =>
        prev.map((n) =>
          n.id === id ? { ...n, isRead: false } : n
        )
      );
      setUnreadCount((c) => c + 1);
    }
  }, [items]);

  const markAllRead = useCallback(async () => {
    const previous = items;
    const prevCount = unreadCount;
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setUnreadCount(0);
    try {
      await markAllNotificationsRead();
    } catch {
      setItems(previous);
      setUnreadCount(prevCount);
    }
  }, [items, unreadCount]);

  const removeOne = useCallback(async (id: string) => {
    setItems((prev) => prev.filter((n) => n.id !== id));
    try {
      await deleteNotification(id);
    } catch {
      // refetch on failure
      load({ reset: true });
    }
  }, [load]);

  // const loadMore = useCallback(() => {
  //   if (!hasMore || loading) return;
  //   const next = page + 1;
  //   setPage(next);
  //   // load uses `page` state → next render will fetch
  // }, [hasMore, loading, page]);
  const loadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    const nextPage = page + 1;
    try {
      setLoading(true);
      const data = await getNotifications({page: nextPage, limit: 15, filter, type: typeFilter,});
      setItems((prev) => [...prev, ...data.items]);
      setUnreadCount(data.unreadCount);
      setPage(nextPage);
      setHasMore(data.pagination.page < data.pagination.pages);
    } catch (err: any) {
      setError(err?.message || 'Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [page, hasMore, loading, filter, typeFilter]);

  return {
    items,
    unreadCount,
    loading,
    error,
    hasMore,
    filter,
    setFilter,
    typeFilter,
    setTypeFilter,
    markRead,
    markAllRead,
    removeOne,
    loadMore,
    refresh: () => load({ reset: true }),
  };
}